import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {AppPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {RepositionPopover} from '../../../../../libs/shared/ui/src/components/popovers/repositionPopover';
import {Anchor} from '../../../../../libs/shared/ui/src/components/reposition/repositionHelpers';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {useWindowDimensions} from '../../../../../libs/shared/ui/src/hooks/domHookHelpers';
import {shadows} from '../../../../../libs/shared/ui/src/styles/colorStyles';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {AdminOnboardingGuide} from '../../../onboarding/admin/adminOnboardingGuideV2';
import {OnboardingTaskGroup} from '../../../onboarding/admin/tasks/onboardingTasks';
import {topBarHeight} from '../topBarHelpers';
import {SetupGuideHelpFooter} from './setupGuideHelpFooter';

/*
 * Props.
 */

export interface OnboardingWidget {
  previousTaskGroups: ReadonlyArray<OnboardingTaskGroup>;
  taskGroups: ReadonlyArray<OnboardingTaskGroup>;
  anchor: Anchor;
  onRequestClose: () => void;
  panelId: AppPanelId;
}

const StyledWidgetDiv = styled.div`
  width: 600px;

  position: relative;
  bottom: 11px;
  margin-right: -4px;

  ${(p) => applyDropdownStyles()}
`;

function applyDropdownStyles() {
  return css`
    background-color: ${(p) => p.theme.foreground};
    border-radius: 16px;
    ${shadows.dialogsAndDropdowns};
    bottom: 0;
    margin-top: 8px;
  `;
}

const StyledCloseButton = styled(ChromelessButton)`
  position: absolute;
  top: 16px;
  right: 16px;
  border-radius: 10px;

  width: 30px;
  height: 30px;
  ${center()};

  color: ${(p) => p.theme.greys.shade80};

  &:hover {
    color: ${(p) => p.theme.greys.shade70};
    background: ${(p) => p.theme.alphas.gray10};
  }
`;

const StyledOnboardingContainerDiv = styled.div`
  padding: 30px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: auto;
  height: 100%;
`;

const StyledSetupGuideHelpFooter = styled(SetupGuideHelpFooter)`
  margin-top: 5px;
`;

/*
 * Component.
 */

export const OnboardingWidget: FC<OnboardingWidget> = (props) => {
  const {panelId, onRequestClose} = props;

  const [, windowHeight] = useWindowDimensions();

  // Subtract an extra 10 pixels so as to not fill up the entire available space.
  const calculatedChecklistMaxHeight = windowHeight - topBarHeight - 10;

  const renderSetupChecklist = () => (
    <StyledOnboardingContainerDiv style={{maxHeight: calculatedChecklistMaxHeight}}>
      <AdminOnboardingGuide
        previousTaskGroups={props.previousTaskGroups}
        taskGroups={props.taskGroups}
        onRequestClose={onRequestClose}
      />
      <StyledSetupGuideHelpFooter panelId={panelId} onRequestClose={onRequestClose} />
    </StyledOnboardingContainerDiv>
  );

  return (
    <RepositionPopover
      anchor={props.anchor}
      onRequestClose={props.onRequestClose}
      placement="bottom-end"
      isInteractive
      isExclusive
    >
      <StyledWidgetDiv>
        <StyledCloseButton onClick={props.onRequestClose}>
          <TooltipCoordinator
            render={() => <Trans id="onboarding-widget-close">Close</Trans>}
            placement="top"
          >
            <Icon name="close" />
          </TooltipCoordinator>
        </StyledCloseButton>
        {renderSetupChecklist()}
      </StyledWidgetDiv>
    </RepositionPopover>
  );
};
